import { Fragment, useEffect, useRef, useState } from "react";
import "./index.css";
import * as CodeMirror from "codemirror/lib/codemirror";
import "codemirror/lib/codemirror.css";
import "codemirror/mode/htmlmixed/htmlmixed";
import "codemirror/theme/monokai.css";
import "codemirror/addon/scroll/simplescrollbars";
import "codemirror/addon/scroll/simplescrollbars.css";
import { CODE as CODE_TOUCH } from "./touchevent";
import { Button, Message, PageHeader, Radio } from "@arco-design/web-react";
import "@arco-design/web-react/dist/css/arco.css";
import copy from "copy-to-clipboard";
import axios from "axios";
import { code as CODE_SWIPER } from "./tiktokCode";
import { getQueryParam, updatePlaygroundUrl } from "./url";

Message.config({ maxCount: 1 });

const MOBILE_DEMO_MODES = [
  "default",
  "mobile",
  "portrait",
  "vod",
  "pip",
  "shopping",
  "feed",
  "loader",
];
const LOADER_HIGHLIGHT_LINES = [
  ...Array.from({ length: 16 }, (_, index) => index + 27),
  83,
  84,
  85,
  109,
  110,
  111,
];
const LOADER_HIGHLIGHT_CLASS = "cm-loader-add-line-background";

const getInitialDemoMode = () => {
  const demoMode = getQueryParam("demo");

  return MOBILE_DEMO_MODES.includes(demoMode) ? demoMode : "default";
};

const Index = props => {
  const [iframeSrc, setIframeSrc] = useState("");
  const [demoLoading, setDemoLoading] = useState(true);
  const [edited, setEdited] = useState(false);
  const [currentTab, setCurrentTab] = useState(getInitialDemoMode);
  const cm = useRef(null);
  const code = useRef({});

  useEffect(() => {
    initCodeMirror();
    closeLoading();
    readFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readFile = () => {
    Promise.all([
      axios.get("/demo/sdk/index.mobile.html"),
      axios.get("/demo/sdk/index.mobile.loader.html"),
      axios.get("/demo/sdk/index.mobile.all-portrait.html"),
      axios.get("/demo/sdk/index.mobile.all.html"),
      axios.get("/demo/sdk/index.mobile.pip.html"),
      axios.get("/demo/sdk/index.mobile.shopping.html"),
      axios.get("/demo/sdk/index.mobile.vodPlayer.html"),
      axios.get("/demo/sdk/tiktok.swiper.html"),
    ]).then(
      ([
        defaultRes,
        loaderRes,
        portraitRes,
        mobileRes,
        pipRes,
        shoppingRes,
        vodRes,
        feedListRes,
      ]) => {
        code.current.CODE = defaultRes.data;
        code.current.CODE_LOADER = loaderRes.data;
        code.current.CODE_PORTRAIT = portraitRes.data;
        code.current.CODE_MOBILE = mobileRes.data;
        code.current.CODE_PIP = pipRes.data;
        code.current.CODE_SHOPPING = shoppingRes.data;
        code.current.VOD = vodRes.data;
        code.current.CODE_FEED_LIST = feedListRes.data;
        applyDemo(currentTab, false);
      }
    );
  };

  const getDemoCode = mode => {
    switch (mode) {
      case "portrait":
        return code.current.CODE_PORTRAIT;
      case "mobile":
        return code.current.CODE_MOBILE;
      case "pip":
        return code.current.CODE_PIP;
      case "feed":
        return code.current.CODE_FEED_LIST;
      case "loader":
        return code.current.CODE_LOADER;
      case "shopping":
        return code.current.CODE_SHOPPING;
      case "vod":
        return code.current.VOD;
      default:
        return code.current.CODE;
    }
  };

  const initCodeMirror = () => {
    cm.current = CodeMirror.fromTextArea(document.getElementById("editor"), {
      mode: "htmlmixed",
      theme: "monokai",
      extraKeys: { Ctrl: "autocomplete" },
      lineNumbers: true,
      value: "",
      lineWrapping: false,
      scrollbarStyle: "overlay",
    });
    cm.current.setSize("", "100%");
    cm.current.on("changes", () => setEdited(true));
  };

  const clearLoaderLineHighlights = () => {
    LOADER_HIGHLIGHT_LINES.forEach(line => {
      cm.current.removeLineClass(line - 1, "background", LOADER_HIGHLIGHT_CLASS);
    });
  };

  const applyLoaderLineHighlights = () => {
    LOADER_HIGHLIGHT_LINES.forEach(line => {
      cm.current.addLineClass(line - 1, "background", LOADER_HIGHLIGHT_CLASS);
    });
  };

  const applyDemo = (mode, shouldUpdateUrl = true) => {
    const str = getDemoCode(mode);

    if (!str) {
      return;
    }

    cm.current.setOption("mode", mode === "feed" ? "jsx" : "htmlmixed");
    closeLoading();
    setIframeSrc(str);
    clearLoaderLineHighlights();
    cm.current.doc.setValue(mode === "feed" ? CODE_SWIPER : str);
    if (mode === "loader") {
      applyLoaderLineHighlights();
      cm.current.scrollTo(null, cm.current.heightAtLine(21, "local"));
    }
    setCurrentTab(mode);
    setEdited(false);

    if (shouldUpdateUrl) {
      updatePlaygroundUrl({ platform: "mobile", demo: mode });
    }
  };

  const changeDemo = mode => {
    applyDemo(mode);
  };

  const goNow = () => {
    setIframeSrc(cm.current.doc.getValue());
    setEdited(false);
    closeLoading();
  };

  const closeLoading = () => {
    setDemoLoading(true);
    setTimeout(() => setDemoLoading(false), 2000);
  };

  const handleCopy = () => {
    copy(cm.current.doc.getValue());
    Message.success("复制成功");
  };

  return (
    <div className="container">
      <PageHeader
        style={{ background: "var(--color-bg-2)" }}
        title="火山引擎企业直播 Web SDK"
        subTitle={
          <Fragment>
            移动端demo playground &nbsp;&nbsp;
            <a
              target="_blank"
              href="https://www.volcengine.com/docs/3019/101258"
              style={{
                color: "rgb(var(--primary-6))",
                textDecoration: "none",
              }}
              rel="noreferrer"
            >
              接入文档
            </a>
            <Button
              type="primary"
              style={{ marginLeft: 12 }}
              onClick={() => {
                props.setIsPc(true);
                updatePlaygroundUrl({ platform: "pc", demo: "default" });
              }}
            >
              切换至PC Demo
            </Button>
          </Fragment>
        }
        extra={
          <div>
            <Button
              type="primary"
              style={{ marginRight: 12 }}
              onClick={goNow}
              loading={demoLoading}
              disabled={!edited}
            >
              立即预览
            </Button>
            <Button type="outline" style={{ marginRight: 12 }} onClick={handleCopy}>
              一键复制
            </Button>
            <Radio.Group
              mode="fill"
              type="button"
              size="large"
              value={currentTab}
              onChange={changeDemo}
            >
              <Radio value="default">标准</Radio>
              <Radio value="mobile">标准整页</Radio>
              <Radio value="portrait">竖屏</Radio>
              <Radio value="vod">点播播放器</Radio>
              <Radio value="pip">画中画</Radio>
              <Radio value="shopping">电商</Radio>
              <Radio value="feed">Feed 流</Radio>
              <Radio value="loader">Loader 接入</Radio>
            </Radio.Group>
          </div>
        }
      />
      <div className="bottom-container">
        <div className="iframe-container">
          {iframeSrc && (
            <iframe
              style={{ width: 412, height: 800 }}
              title="mobile preview"
              srcDoc={iframeSrc.replace("<title>企业直播WEB SDK DEMO</title>", CODE_TOUCH)}
            ></iframe>
          )}
          <div style={{ color: "#fff", marginTop: "12px" }}>
            * PC端点击事件与移动端略有不同，部分功能在此不可用
          </div>
        </div>
        <div className="cmContainer">
          <textarea id="editor"></textarea>
        </div>
      </div>
    </div>
  );
};

export default Index;
